import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  CheckCircleIcon,
  XCircleIcon,
  KeyIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  XMarkIcon,
  CalendarIcon,
  ClipboardDocumentIcon
} from '@heroicons/react/24/outline';
import Swal from 'sweetalert2';
import { tokenApi } from '../../../repositories/token';
import {columnMapApi} from '../../../repositories/columnMap'
import { Link } from 'react-router-dom';
import CreateTokenModal from '../../../components/modal/CreateTokenModal';
import UpdateTokenModal from '../../../components/modal/UpdateTokenModal';
import IntegrationModal from '../../../components/modal/IntegrationModal';
import { set } from 'date-fns';

interface ApiToken {
  id: string;
  name: string;
  status: boolean;
  scopes: string;
  allowed_domains: string[];
  expires_at: string;
  createdAt: string;
  token?: string;
}

interface CreateTokenForm {
  name: string;
  domains: string;
  expirationDate: string;
  readPermission: boolean;
  writePermission: boolean;
}

interface UpdateTokenForm {
  name: string;
  domains: string;
  expirationDate: string;
  readPermission: boolean;
  writePermission: boolean;
  status: boolean;
}
const isValidDomainWithWildcard = (domain: string): boolean => {
  const trimmedDomain = domain.trim();
  // Allow only "*" as special case
  if (trimmedDomain === '*') {
    return true;
  }
  // Normal domain regex (no wildcard allowed here)
  const domainRegex = /^([a-zA-Z0-9-]{1,63}\.)+[a-zA-Z]{2,}$/;
  return domainRegex.test(trimmedDomain);
};

const Token: React.FC = () => {
  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTokenModal, setshowTokenModal] = useState(false);
  const [showExpirationDateInput, setShowExpirationDateInput] = useState(false);
  const [showToken, setShowToken] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [createdToken, setCreatedToken] = useState("");

  // Update token information
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedUpdateTokenInformation, setSelectedUpdateTokenInfomation] = useState<ApiToken | null>(null);

  // Domain name in separated form for create
  const [domainTags, setDomainTags] = useState<string[]>([]);
  const [domainInput, setDomainInput] = useState('');
  const [domainError, setDomainError] = useState('');

  // Domain name in separated form for update
  const [updateDomainTags, setUpdateDomainTags] = useState<string[]>([]);
  const [updateDomainInput, setUpdateDomainInput] = useState('');
  const [updateDomainError, setUpdateDomainError] = useState('');

  // Editing domain state
  const [editingDomainIdx, setEditingDomainIdx] = useState<number | null>(null);
  const [editingDomainValue, setEditingDomainValue] = useState('');

  const [updateForm, setUpdateForm] = useState<UpdateTokenForm>({
    name: '',
    domains: '',
    expirationDate: '',
    readPermission: false,
    writePermission: false,
    status: true,
  });

  const [createForm, setCreateForm] = useState<CreateTokenForm>({
    name: '',
    domains: '',
    expirationDate: '',
    readPermission: false,
    writePermission: false,
  });
  const handleMappedData = async (mappedData: any) => {
    const payload = {
      tokenId: openMenuId,
      platform: integrationType,
      mapping: mappedData
    };
    setShowIntegrationModal(false);

    try {
      // 1. Check if mapping already exists
      const checkRes = await columnMapApi.check(openMenuId!, integrationType);
      const exist = checkRes.data.exist;

      if (exist) {
        // 2. Ask user if they want to replace
        const result = await Swal.fire({
          title: `${integrationType} Mapping Already Exists`,
          text: `You have already integrated ${integrationType} for this token. Do you want to replace it?`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Replace",
          cancelButtonText: "Keep Existing"
        });

        if (result.isConfirmed) {
          // 3. Replace (update) mapping
          try {
            await columnMapApi.update(checkRes.data?.data?.id, payload);
            Swal.fire({
              title: "Replaced!",
              text: "Integration mapping has been replaced.",
              icon: "success",
              timer: 2000,
              showConfirmButton: false,
            });
          } catch (error: any) {
            const errorMessage = error?.response?.data?.message || "Failed to update mapping.";
            Swal.fire({
              title: "Error!",
              text: errorMessage,
              icon: "error",
            });
          }
        } else {
          Swal.fire({
            title: "Kept Existing",
            text: "Your previous integration mapping was kept.",
            icon: "info",
            timer: 2000,
            showConfirmButton: false,
          });
        }
      } else {
        // 4. Save new mapping
        try {
          await columnMapApi.save(payload);
          Swal.fire({
            title: "Saved!",
            text: "Integration mapping has been saved.",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
        } catch (error: any) {
          const errorMessage = error?.response?.data?.message || "Failed to save mapping.";
          Swal.fire({
            title: "Error!",
            text: errorMessage,
            icon: "error",
          });
        }
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Something went wrong.";
      Swal.fire({
        title: "Error!",
        text: errorMessage,
        icon: "error",
      });
    }

    return mappedData;
  }

  const [showIntegrationModal, setShowIntegrationModal] = useState(false);
  const [integrationType, setIntegrationType] = useState<"Airtable" | "Google Sheet">("Airtable");

  const fetchTokens = async () => {
    setLoading(true);
    try {
      const res = await tokenApi.getAll();
      setTokens(res.data.data);
    } catch (error) {
      setTokens([]);
    } finally {
      setLoading(false);
    }
  };

  const deleteTokens = async (id: string) => {
    setLoading(true);
    try {
      await tokenApi.delete(id);
    } catch (error) {
      setTokens([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTokens();
  }, []);



  // Open update modal and fill form
  const handleOpenUpdateModal = (token: any) => {
    setSelectedUpdateTokenInfomation(token);
    setOpenMenuId(null);
    setUpdateForm({
      name: token.name || '',
      domains: Array.isArray(token.allowed_domains)
        ? token.allowed_domains.join(', ')
        : token.allowed_domains || '',
      expirationDate: token.expires_at ? token.expires_at.split('T')[0] : '',
      readPermission: token.scopes?.includes('read') || false,
      writePermission: token.scopes?.includes('write') || false,
      status: token.status,
    });
    setShowExpirationDateInput(!!token.expires_at);
    setUpdateDomainTags(
      Array.isArray(token.allowed_domains)
        ? token.allowed_domains
        : (token.allowed_domains || '').split(',').map((d: string) => d.trim()).filter(Boolean)
    );
    setUpdateDomainInput('');
    setUpdateDomainError('');
    setShowUpdateModal(true);
  };

  const handleUpdateFormChange = (field: keyof UpdateTokenForm, value: any) => {
    setUpdateForm({ ...updateForm, [field]: value });
  };

  // Update token handler (does NOT update token value)
  const handleUpdateToken = async () => {
    if (!selectedUpdateTokenInformation) return;

    if (!updateForm.name.trim()) {
      Swal.fire({
        title: 'Error!',
        text: 'Token name is required.',
        icon: 'error',
      });
      return;
    }

    if (updateDomainTags.length === 0) {
      Swal.fire({
        title: 'Error!',
        text: 'At least one domain is required.',
        icon: 'error',
      });
      return;
    }
    const invalidDomains = updateDomainTags.filter(domain => !isValidDomainWithWildcard(domain));
    if (invalidDomains.length > 0) {
      Swal.fire({
        title: 'Error!',
        text: `Invalid domain format: ${invalidDomains.join(', ')}. Please use valid domain names (e.g., example.com, *.example.com)`,
        icon: 'error',
      });
      return;
    }

    if (!updateForm.readPermission && !updateForm.writePermission) {
      Swal.fire({
        title: 'Error!',
        text: 'At least one permission must be selected.',
        icon: 'error',
      });
      return;
    }

    const payload = {
      name: updateForm.name,
      allowed_domains: updateDomainTags.join(','),
      expires_at: showExpirationDateInput && updateForm.expirationDate
        ? updateForm.expirationDate
        : null,
      scopes: [
        ...(updateForm.readPermission ? ['read'] : []),
        ...(updateForm.writePermission ? ['write'] : []),
      ].join(','),
      status: updateForm.status
    };

    try {
      await tokenApi.update(selectedUpdateTokenInformation.id, payload);
      setShowUpdateModal(false);
      Swal.fire({
        title: 'Updated!',
        text: 'API token has been updated successfully.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
      });
      fetchTokens();
    } catch (error: any) {
      const responseData = error?.response?.data;
      let errorMessage = "Something went wrong.";

      if (Array.isArray(responseData?.errors) && responseData.errors.length > 0) {
        errorMessage = responseData.errors.join('\n');
      } else if (responseData?.message) {
        errorMessage = responseData.message;
      }

      Swal.fire({
        title: 'Error!',
        text: errorMessage,
        icon: 'error',
      });
    }
  };

  // Helper functions
  const getActiveTokens = () => tokens.filter(token => token.status).length;
  const getExpiredTokens = () => {
    const today = new Date();
    return (tokens || []).filter(token =>
      token.expires_at && new Date(token.expires_at) < today
    ).length;
  };
  const getTotalTokens = () => tokens.length;

  const formatDateTime = (isoString: string) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US');
  };

  const handleCreateToken = () => {
    // Reset all create form state
    setCreateForm({
      name: '',
      domains: '',
      expirationDate: '',
      readPermission: false,
      writePermission: false,
    });
    setDomainTags([]);
    setDomainInput('');
    setDomainError('');
    setShowExpirationDateInput(false);
    setShowCreateModal(true);
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    // Reset create form state
    setCreateForm({
      name: '',
      domains: '',
      expirationDate: '',
      readPermission: false,
      writePermission: false,
    });
    setDomainTags([]);
    setDomainInput('');
    setDomainError('');
  };

  const handleshowTokenModal = () => {
    setshowTokenModal(true);
  };

  const handlecloseTokenModal = () => {
    setshowTokenModal(false);
  };

  const handleFormChange = (field: keyof CreateTokenForm, value: string | boolean) => {
    setCreateForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmitToken = async () => {
    if (!createForm.name.trim()) {
      Swal.fire({
        title: 'Error!',
        text: 'Token name is required.',
        icon: 'error',
      });
      return;
    }

    if (domainTags.length === 0) {
      Swal.fire({
        title: 'Error!',
        text: 'At least one domain is required.',
        icon: 'error',
      });
      return;
    }
    const invalidDomains = domainTags.filter(domain => !isValidDomainWithWildcard(domain));
    if (invalidDomains.length > 0) {
      Swal.fire({
        title: 'Error!',
        text: `Invalid domain format: ${invalidDomains.join(', ')}. Please use valid domain names (e.g., example.com, *.example.com)`,
        icon: 'error',
      });
      return;
    }

    if (!createForm.readPermission && !createForm.writePermission) {
      Swal.fire({
        title: 'Error!',
        text: 'At least one permission must be selected.',
        icon: 'error',
      });
      return;
    }

    const payload = {
      name: createForm.name,
      allowed_domains: domainTags.join(','),
      scopes: [
        ...(createForm.readPermission ? ['read'] : []),
        ...(createForm.writePermission ? ['write'] : [])
      ].join(','),
      expires_at: showExpirationDateInput && createForm.expirationDate
        ? createForm.expirationDate
        : null,
    };

    try {
      const res = await tokenApi.create(payload);
      setCreatedToken(res.data.data.token);
      setDomainTags([]);
      setDomainInput('');
      handleshowTokenModal();
      handleCloseModal();
      await fetchTokens();
    } catch (error: any) {
      const responseData = error?.response?.data;
      let errorMessage = "Something went wrong.";

      if (Array.isArray(responseData?.errors) && responseData.errors.length > 0) {
        errorMessage = responseData.errors.join('\n');
      } else if (responseData?.message) {
        errorMessage = responseData.message;
      }

      Swal.fire({
        title: 'Error!',
        text: errorMessage,
        icon: 'error',
      });
    }
  };

  const handleDeleteToken = async (tokenId: string) => {
    setOpenMenuId(null);
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This action will delete the API token permanently.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, Delete it!',
    });

    if (result.isConfirmed) {
      try {
        await deleteTokens(tokenId);
        fetchTokens();
        setOpenMenuId(null);
        Swal.fire({
          title: 'Deleted!',
          text: 'API token has been deleted successfully.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (error) {
        Swal.fire({
          title: 'Error!',
          text: 'Failed to delete token.',
          icon: 'error',
        });
      }
    }
  };

  const toggleTokenVisibility = (tokenId: string) => {
    setShowToken(showToken === tokenId ? null : tokenId);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    Swal.fire({
      title: 'Copied!',
      text: 'Token copied to clipboard.',
      icon: 'success',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 2000,
    });
  };

  if (loading) {
    return (
      <div className="max-w-6xl p-4 bg-white rounded-lg shadow-sm sm:p-6">
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl p-4 bg-white rounded-lg shadow-sm sm:p-6">
      {/* Header Section */}
      <div className="flex flex-col justify-between mb-6 sm:flex-row sm:items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">API Token Management</h2>
          <p className="mt-1 text-sm text-gray-600">
            Manage your API tokens and access permissions.
          </p>
        </div>
        <button
          onClick={handleCreateToken}
          className="inline-flex items-center px-4 py-2 mt-4 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm sm:mt-0 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
        >
          <PlusIcon className="w-4 h-4 mr-2" />
          Create Token
        </button>
      </div>

      {/* Token Summary Cards */}
      <div className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-3">
        {/* Active Tokens */}
        <div className="p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="flex items-center justify-center w-8 h-8 bg-green-100 rounded-full">
              <CheckCircleIcon className="w-5 h-5 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Tokens</p>
              <p className="text-2xl font-bold text-gray-900">{getActiveTokens()}</p>
            </div>
          </div>
        </div>

        {/* Expired Tokens */}
        <div className="p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="flex items-center justify-center w-8 h-8 bg-red-100 rounded-full">
              <XCircleIcon className="w-5 h-5 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Expired Tokens</p>
              <p className="text-2xl font-bold text-gray-900">{getExpiredTokens()}</p>
            </div>
          </div>
        </div>

        {/* Total Tokens */}
        <div className="p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full">
              <KeyIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Tokens</p>
              <p className="text-2xl font-bold text-gray-900">{getTotalTokens()}</p>
            </div>
          </div>
        </div>
      </div>
      {/* API Token Table */}
      {tokens.length > 0 ? (
        <div className="overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">API Tokens</h3>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                    Name
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                    Scopes
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                    Domains
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                    Created
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                    Expires
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tokens.map((token:any, idx) => (
                 
                  
                  <tr key={token.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <KeyIcon className="w-5 h-5 mr-3 text-gray-400" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {token.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {token.id}...
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${token.status === true
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                        }`}>
                        {token.status === true ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex space-x-1">
                        {token.scopes.split(',').map((scope:any, index:number) => (
                          <span
                            key={index}
                            className="px-2 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-full"
                          >
                            {scope.trim()}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="max-w-xs text-sm text-gray-900 truncate">
                        {Array.isArray(token.allowed_domains)
                          ? token.allowed_domains.join(', ')
                          : token.allowed_domains || 'No domains'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatDateTime(token.createdAt)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {token.expires_at
                          ? new Date(token.expires_at) < new Date()
                            ? "Expired"
                            : formatDateTime(token.expires_at)
                          : "-----"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {token.status === true && token.token && (
                          <>
                            <button
                              onClick={() => toggleTokenVisibility(token.id)}
                              className="text-gray-400 hover:text-gray-600"
                              title={showToken === token.id ? 'Hide token' : 'Show token'}
                            >
                              {showToken === token.id ? (
                                <EyeSlashIcon className="w-4 h-4" />
                              ) : (
                                <EyeIcon className="w-4 h-4" />
                              )}
                            </button>
                            {showToken === token.id && (
                              <button
                                onClick={() => copyToClipboard(token.token!)}
                                className="text-xs text-blue-600 hover:text-blue-800"
                              >
                                Copy
                              </button>
                            )}
                          </>
                        )}
                        <div className="relative inline-block text-left">
                          <button
                            onClick={() =>
                              setOpenMenuId(openMenuId === token.id ? null : token.id)
                            }
                            className="flex items-center justify-center w-6 h-6 font-bold text-black hover:text-gray-700 focus:outline-none"
                            type="button"
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <circle cx="12" cy="5" r="1.5" />
                              <circle cx="12" cy="12" r="1.5" />
                              <circle cx="12" cy="19" r="1.5" />
                            </svg>
                          </button>

                          {openMenuId === token.id && (
                            <div
                              className={`absolute z-30 min-w-48 bg-white border border-gray-200 rounded-md shadow-lg ${idx === tokens.length - 1 ? 'bottom-full mb-[-6] ' : 'top-full mt-2'
                                } right-0`}
                            >
                              {token.allowed_domains.split(",").includes("airtable.com")&&<button
                                className="block w-full px-4 py-2 text-sm text-left text-teal-700 hover:bg-gray-100"
                                onClick={() => {
                                  setIntegrationType("Airtable");
                                  setShowIntegrationModal(true);
                                  setOpenMenuId(token.id);
                                }}
                              >
                                Connect with Airtable 
                              </button>}
                              {token.allowed_domains.split(",").includes("script.google.com")&&<button
                                className="block w-full px-4 py-2 text-sm text-left text-blue-700 hover:bg-gray-100"
                                onClick={() => {
                                  setIntegrationType("Google Sheet");
                                  setShowIntegrationModal(true);
                                  setOpenMenuId(token.id);
                                }}
                              >
                                Connect with Google Sheet
                              </button>}
                              <button
                                className="block w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
                                onClick={() => {
                                  handleOpenUpdateModal(token);
                                }}
                              >
                                Update
                              </button>
                              <button
                                onClick={() => {
                                  handleDeleteToken(token.id);
                                }}
                                className="block w-full px-4 py-2 text-sm text-left text-red-600 hover:bg-gray-100"
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* Empty State */
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="px-6 py-12 text-center">
            <div className="flex items-center justify-center w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full">
              <KeyIcon className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="mb-2 text-xl font-semibold text-gray-900">No API tokens yet</h3>
            <p className="max-w-md mx-auto mb-6 text-gray-500">
              Create your first API token to start integrating with our services.
              API tokens allow secure access to our APIs and services.
            </p>
            <div className="flex flex-col justify-center gap-3 sm:flex-row">
              <button
                onClick={handleCreateToken}
                className="inline-flex items-center px-6 py-3 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                Create Your First Token
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Token Modal */}
      <CreateTokenModal
        showModal={showCreateModal}
        onClose={handleCloseModal}
        createForm={createForm}
        onFormChange={handleFormChange}
        onSubmit={handleSubmitToken}
        showExpirationDateInput={showExpirationDateInput}
        setShowExpirationDateInput={setShowExpirationDateInput}
        domainTags={domainTags}
        setDomainTags={setDomainTags}
        domainInput={domainInput}
        setDomainInput={setDomainInput}
        domainError={domainError}
        setDomainError={setDomainError}
      />

      {/* Update Token Modal */}
      <UpdateTokenModal
        showModal={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
        updateForm={updateForm}
        onFormChange={handleUpdateFormChange}
        onSubmit={handleUpdateToken}
        showExpirationDateInput={showExpirationDateInput}
        setShowExpirationDateInput={setShowExpirationDateInput}
        updateDomainTags={updateDomainTags}
        setUpdateDomainTags={setUpdateDomainTags}
        updateDomainInput={updateDomainInput}
        setUpdateDomainInput={setUpdateDomainInput}
        updateDomainError={updateDomainError}
        setUpdateDomainError={setUpdateDomainError}
        editingDomainIdx={editingDomainIdx}
        setEditingDomainIdx={setEditingDomainIdx}
        editingDomainValue={editingDomainValue}
        setEditingDomainValue={setEditingDomainValue}
      />

      {/* Show Token Modal */}
      {showTokenModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-lg p-6 bg-white rounded-lg shadow-lg">
            <div className="mb-6 text-center">
              <CheckCircleIcon className="w-10 h-10 mx-auto mb-2 text-green-500" />
              <h2 className="text-xl font-semibold text-black">Token Created Successfully!</h2>
              <p className="mt-1 text-sm text-gray-600">
                Please save this token securely. <span className="font-semibold text-yellow-600">This is the only time it will be displayed.</span>
              </p>
            </div>

            {/* Token Box */}
            <div className="mb-4">
              <div className='flex items-center justify-between'>
                <p className='text-sm font-semibold'>Your API Token:</p>
                <button
                  onClick={() => copyToClipboard(createdToken)}
                  className="flex items-center gap-1 px-2 py-2 text-sm text-blue-500 hover:text-blue-600 "
                >
                  <ClipboardDocumentIcon className="w-5 h-5" />copy
                </button>
              </div>
              <div className="relative p-3 overflow-y-auto font-mono text-xs text-green-700 break-words bg-gray-100 border border-gray-300 rounded-md max-h-40">
                {createdToken}
              </div>
            </div>

            {/* Security Notice */}
            <div className="p-4 mb-6 text-sm text-yellow-800 bg-yellow-100 border border-yellow-300 rounded-md">
              <strong className="block mb-1 font-medium">Important Security Notice</strong>
              Make sure to copy and save this token in a secure location. Once you close this dialog, the token will not be displayed again.
            </div>

            {/* Footer Buttons */}
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => copyToClipboard(createdToken)}
                className="flex items-center gap-2 px-2 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                <ClipboardDocumentIcon className="w-5 h-5" />
                Copy Token
              </button>
              <button
                onClick={handlecloseTokenModal}
                className="px-2 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                I've Saved It
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Integration Modal */}
      <IntegrationModal
        isOpen={showIntegrationModal}
        onClose={handleMappedData}
        integrationType={integrationType}
        closeModal={setShowIntegrationModal}
      />
    </div>
  );
};

export default Token;